import React from 'react';
import { Card, CardContent, Typography, Box, Grid2, alpha } from '@mui/material';
import ChartHeader from './ChartHeader';

interface CardData {
    cardTitle: string;
    score: string;
    lable: string;
    targetLine: string;
    targetSubLine: string;
    description: string;
}

interface CompareInfoCardsProps {
    title: string;
    description: string;
    details?: any;
    data: CardData[];
    onDetailsClick?: (data?: any) => void;
}

// Individual Info Card Component
const InfoCard: React.FC<{ item: CardData; onDetailsClick?: (data?: any) => void }> = ({ item, onDetailsClick }) => {
    // Determine color based on targetSubLine status
    const getStatusColor = () => {
        const status = item.targetSubLine.toLowerCase();
        if (status.includes('within range') || status.includes('good')) {
            return '#10b981'; // green
        } else if (status.includes('below') || status.includes('poor')) {
            return '#ef4444'; // red
        } else if (status.includes('above') || status.includes('warning')) {
            return '#f59e0b'; // orange
        }
        return '#f59e0b'; // gray
    };

    const statusColor = getStatusColor();

    const handleCardClick = () => {
        if (onDetailsClick) {
            onDetailsClick(item);
        }
        console.log('Clicked Info Card:');
        console.log('Card Title:', item.cardTitle);
        console.log('Score:', item.score);
        console.log('Label:', item.lable);
        console.log('Target Line:', item.targetLine);
        console.log('Target SubLine:', item.targetSubLine);
        console.log('Description:', item.description);
        console.log('Full Data:', item);
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: alpha(statusColor, 0.2),
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: `0 8px 24px ${alpha(statusColor, 0.15)}`,
                    transform: 'translateY(-4px)',
                    borderColor: alpha(statusColor, 0.4),
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${statusColor}, ${alpha(statusColor, 0.6)})`,
                },
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                {/* Card Title */}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1.5,
                    }}
                >
                    {item.cardTitle}
                </Typography>

                {/* Score */}
                <Typography
                    variant="h3"
                    sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: statusColor,
                        mb: 0.5,
                        lineHeight: 1.2,
                    }}
                >
                    {item.score}
                </Typography>

                {/* Label */}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.875rem',
                        color: '#475569',
                        fontWeight: 500,
                        mb: 1.5,
                    }}
                >
                    {item.lable}
                </Typography>

                {/* Target Line */}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                        mb: 0.25,
                    }}
                >
                    {item.targetLine}
                </Typography>

                {/* Target SubLine with Status Badge */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        backgroundColor: alpha(statusColor, 0.1),
                        mb: 1.5,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: statusColor,
                        }}
                    >
                        {item.targetSubLine}
                    </Typography>
                </Box>

                {/* Description */}
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.7rem',
                        color: '#94a3b8',
                        fontStyle: 'italic',
                        display: 'block',
                    }}
                >
                    {item.description}
                </Typography>
            </CardContent>
        </Card>
    );
};

const CompareInfoCards: React.FC<CompareInfoCardsProps> = ({
    title,
    description,
    details,
    data,
    onDetailsClick
}) => {
    // Calculate grid columns based on data length
    const getGridColumns = () => {
        const dataLength = data.length;
        if (dataLength === 1) return 12; // Full width for single card
        if (dataLength === 2) return 6;  // Half width for 2 cards
        if (dataLength === 3) return 4;  // Third width for 3 cards
        if (dataLength === 4) return 3;  // Quarter width for 4 cards
        return 2.4; // 5 cards per row (12/5 = 2.4)
    };

    const gridColumns = getGridColumns();

    return (
        <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
            <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                {/* Header Section */}
                <ChartHeader title={title} description={description} details={details} />

                {details?.label && (
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
                        {details.label || ''}
                    </Typography>
                )}
                {details?.subLabel && (
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
                        {details.subLabel || ''}
                    </Typography>
                )}

                {details?.bottomDescription && (
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center', marginTop: 1 }}>
                        {details.bottomDescription || ''}
                    </Typography>
                )}

                {/* Cards Grid */}
                <Grid2 container spacing={2}>
                    {data && data.length > 0 ? (
                        data.map((item, index) => (
                            <Grid2 size={{ xs: 12, sm: 6, md: gridColumns }} key={index}>
                                <InfoCard item={item} onDetailsClick={onDetailsClick} />
                            </Grid2>
                        ))
                    ) : (
                        <Grid2 size={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                No data available
                            </Typography>
                        </Grid2>
                    )}
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default CompareInfoCards;
